export const INSERTION_LANES = ['left', 'center', 'right'] as const;

export type InsertionLane = (typeof INSERTION_LANES)[number];

export type CoinInsertionSource = 'keyboard' | 'pointer' | 'api';

export type CoinInsertionReadiness =
  | 'ready'
  | 'cooldown'
  | 'no-coins'
  | 'paused'
  | 'disabled'
  | 'disposed';

export type CoinInsertionRejectReason =
  | 'cooldown'
  | 'no-coins'
  | 'paused'
  | 'disabled'
  | 'disposed'
  | 'rejected'
  | 'request-error'
  | (string & {});

/**
 * A simulation-facing intent. The host owns the balance and must atomically
 * consume exactly `cost` when returning an accepted decision.
 */
export interface CoinInsertionIntent {
  readonly id: number;
  readonly lane: InsertionLane;
  readonly source: CoinInsertionSource;
  readonly repeat: boolean;
  readonly cost: 1;
  readonly requestedAt: number;
  readonly balanceBefore: number | null;
}

export interface CoinInsertionDecisionDetail {
  readonly accepted: boolean;
  readonly reason?: CoinInsertionRejectReason;
  /** Optional post-decision balance for feedback and diagnostics. */
  readonly balance?: number;
}

export type CoinInsertionDecision = boolean | CoinInsertionDecisionDetail;

export type CoinInsertionRequestHandler = (
  intent: CoinInsertionIntent,
) => CoinInsertionDecision;

export interface CoinInsertionLaneTelemetry {
  readonly selections: number;
  readonly attempts: number;
  readonly accepted: number;
  readonly rejected: number;
  readonly lastAttemptAt: number | null;
  readonly lastAcceptedAt: number | null;
}

export interface CoinInsertionTelemetry {
  readonly startedAt: number;
  readonly lastEventAt: number | null;
  readonly selectionIntents: number;
  readonly attempts: number;
  /** Number of attempts forwarded to the host's atomic request callback. */
  readonly hostRequests: number;
  readonly accepted: number;
  readonly rejected: number;
  readonly noCoinRejections: number;
  readonly lanes: Readonly<Record<InsertionLane, CoinInsertionLaneTelemetry>>;
  readonly lastAttempt: CoinInsertionIntent | null;
  readonly lastAccepted: CoinInsertionIntent | null;
}

export interface CoinInsertionLaneState extends CoinInsertionLaneTelemetry {
  readonly lane: InsertionLane;
  readonly selected: boolean;
  readonly ready: boolean;
  readonly cooldownRemainingMs: number;
}

export interface CoinInsertionState {
  readonly selectedLane: InsertionLane;
  readonly enabled: boolean;
  readonly paused: boolean;
  readonly disposed: boolean;
  readonly readiness: CoinInsertionReadiness;
  readonly ready: boolean;
  readonly balance: number | null;
  readonly cost: 1;
  readonly cooldownMs: number;
  readonly cooldownRemainingMs: number;
  readonly cooldownUntil: number | null;
  readonly holding: boolean;
  readonly repeatEnabled: boolean;
  readonly repeatActive: boolean;
  readonly lanes: Readonly<Record<InsertionLane, CoinInsertionLaneState>>;
}

export interface CoinInsertionLaneSelectedEvent {
  readonly type: 'lane-selected';
  readonly lane: InsertionLane;
  readonly previousLane: InsertionLane;
  readonly changed: boolean;
  readonly source: CoinInsertionSource;
  readonly at: number;
}

export interface CoinInsertionDropAcceptedEvent {
  readonly type: 'drop-accepted';
  readonly intent: CoinInsertionIntent;
  readonly balanceAfter: number | null;
  readonly cooldownUntil: number | null;
}

export interface CoinInsertionDropRejectedEvent {
  readonly type: 'drop-rejected';
  readonly intent: CoinInsertionIntent;
  readonly reason: CoinInsertionRejectReason;
  readonly balanceAfter: number | null;
}

export type CoinInsertionDropEvent =
  | CoinInsertionDropAcceptedEvent
  | CoinInsertionDropRejectedEvent;

export interface CoinInsertionReadinessEvent {
  readonly type: 'readiness-changed';
  readonly previous: CoinInsertionReadiness;
  readonly readiness: CoinInsertionReadiness;
  readonly at: number;
}

export interface CoinInsertionControlEvent {
  readonly type: 'control-changed';
  readonly action: 'enabled' | 'disabled' | 'paused' | 'resumed' | 'reset';
  readonly at: number;
}

export type CoinInsertionEvent =
  | CoinInsertionLaneSelectedEvent
  | CoinInsertionDropEvent
  | CoinInsertionReadinessEvent
  | CoinInsertionControlEvent;

export interface CoinInsertionRepeatOptions {
  readonly enabled?: boolean;
  /** Delay before the second insertion while held. Defaults to 420 ms. */
  readonly initialDelayMs?: number;
  /** Subsequent repeat interval. Defaults to 180 ms. */
  readonly intervalMs?: number;
}

export interface CoinInsertionOptions {
  /**
   * Atomically checks/consumes host balance and emits the accepted insertion to
   * gameplay. It is called at most once for each forwarded intent.
   */
  readonly requestCoin: CoinInsertionRequestHandler;
  /** Optional live, read-only balance used for `no-coins` readiness/preflight. */
  readonly readBalance?: () => number;
  readonly initialLane?: InsertionLane;
  readonly enabled?: boolean;
  readonly paused?: boolean;
  /** Global insertion cooldown. Defaults to 180 ms. */
  readonly cooldownMs?: number;
  readonly repeat?: boolean | CoinInsertionRepeatOptions;
  /** Installs A/S/D, arrow, Space, and Enter controls. Defaults to true. */
  readonly keyboard?: boolean;
  readonly keyboardTarget?: Window | Document | HTMLElement;
  readonly clock?: () => number;
  readonly onEvent?: (event: CoinInsertionEvent) => void;
  readonly onStateChange?: (state: CoinInsertionState) => void;
  readonly onError?: (error: unknown) => void;
}

export interface CoinInsertionDropOptions {
  readonly lane?: InsertionLane;
  readonly source?: CoinInsertionSource;
  readonly repeat?: boolean;
}

export interface CoinInsertionResetOptions {
  readonly lane?: InsertionLane;
  readonly enabled?: boolean;
  readonly paused?: boolean;
  /** Defaults to true. */
  readonly clearTelemetry?: boolean;
}

export interface CoinInsertionPointerOptions {
  readonly preventDefault?: boolean;
  /** Uses the controller's repeat setting unless explicitly overridden. */
  readonly holdToRepeat?: boolean;
}

export interface CoinInsertionLaneButtonOptions extends CoinInsertionPointerOptions {
  /** When true, the same pointer press selects the lane and inserts a coin. */
  readonly dropOnPress?: boolean;
}

export interface CoinInsertionButtonSet {
  readonly left?: HTMLElement;
  readonly center?: HTMLElement;
  readonly right?: HTMLElement;
  readonly drop?: HTMLElement;
}

export interface CoinInsertionButtonSetOptions {
  readonly laneButtons?: CoinInsertionLaneButtonOptions;
  readonly dropButton?: CoinInsertionPointerOptions;
}

export type CoinInsertionUnbind = () => void;

export type CoinInsertionEventListener = (event: CoinInsertionEvent) => void;

export type CoinInsertionStateListener = (state: CoinInsertionState) => void;
